import clsx from 'clsx';
import { InputText } from 'primereact/inputtext';
import type React from 'react';
import { useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import styles from './search-bar.module.css';

interface PersonResult {
	name: string;
	email: string;
}

interface SearchBarProps {
	width?: string;
	height?: string;
	placeholder?: string;
	containerClassName?: string;
	inputClassName?: string;
	suggestions?: string[] | PersonResult[];
	onSuggestionClick?: (suggestion: string) => void;
}

export const SearchBar = ({
	width,
	height = '56px',
	placeholder = 'Search by name or email',
	containerClassName = '',
	inputClassName = '',
	suggestions = [],
	onSuggestionClick,
}: SearchBarProps) => {
	const [query, setQuery] = useState('');
	const [showSuggestions, setShowSuggestions] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const filteredSuggestions = suggestions.filter((s) => {
		if (typeof s === 'string') {
			return s.toLowerCase().includes(query.toLowerCase());
		}
		return (
			s.name.toLowerCase().includes(query.toLowerCase()) ||
			s.email.toLowerCase().includes(query.toLowerCase())
		);
	});

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setQuery(e.target.value);
	};

	const handleFocus = () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		setShowSuggestions(true);
	};

	const handleSuggestionClick = (suggestion: string) => {
		setQuery(suggestion);
		setShowSuggestions(false);
		onSuggestionClick?.(suggestion);
	};

	return (
		<div
			className={clsx(
				styles.searchbarContainer,
				containerClassName,
				showSuggestions
					? 'rounded-t-[28px] rounded-b-none'
					: 'rounded-full',
			)}
			style={{ width: width || '100%', height }}
		>
			<MdSearch className={styles.searchbarIcon} />
			<InputText
				value={query}
				onChange={handleChange}
				onFocus={() => setShowSuggestions(true)}
				onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
				placeholder={placeholder}
				className={clsx(styles.searchbarInput, inputClassName)}
				style={{ backgroundImage: 'none' }}
			/>

			{showSuggestions && filteredSuggestions.length > 0 && (
				<div
					className={styles.suggestionsContainer}
					onMouseDown={(e) => e.preventDefault()}
				>
					{filteredSuggestions.map((suggestion) => {
						if (typeof suggestion === 'string') {
							return (
								<button
									key={suggestion}
									type="button"
									className={styles.suggestionItem}
									onClick={() =>
										handleSuggestionClick(suggestion)
									}
								>
									{suggestion}
								</button>
							);
						}
						return (
							<button
								key={suggestion.name}
								type="button"
								className={styles.suggestionItem}
								onClick={() =>
									handleSuggestionClick(suggestion.name)
								}
							>
								{suggestion.name}
								<div className={styles.suggestionEmail}>
									{suggestion.email}
								</div>
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};
